import classnames from 'classnames';
import type { IWithQuoteFormWrapperProps } from 'components/shared/WithQuoteFormWrapper/WithQuoteFormWrapper';
import { WithQuoteFormWrapper } from 'components/shared/WithQuoteFormWrapper/WithQuoteFormWrapper';
import { ChangeEvent, FC, FocusEvent, MouseEvent } from 'react';

export interface IListFieldProps<TItem extends IListFieldItem, TValue> {
  className?: string;
  hasEmptyItem?: boolean;
  hasError?: boolean;
  id: string;
  isDisabled?: boolean;
  isEdited?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  items: readonly TItem[];
  label?: string;
  onBlur?: (event: FocusEvent<HTMLSelectElement>) => void;
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  onClick?: (event: MouseEvent<HTMLSelectElement>) => void;
  onCustomizationRemoved?: () => void;
  onFocus?: (event: FocusEvent<HTMLSelectElement>) => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  tabIndex?: number;
  value?: TValue;
}

export interface IListFieldItem {
  label: string;
  value: string | number;
}

export const ListField: FC<IListFieldProps<any, string | number>> = props => {
  const items = props.hasEmptyItem ? [{}, ...props.items] : props.items;
  const size = props.size ? props.size : 'md';
  return (
    <div
      className={classnames('list-field-component px-0', props.className, {
        error: props.hasError,
      })}
      data-e2e={props.id}
    >
      <select
        className={classnames('form-select', `form-select-${size}`, props.className, {
          'border border-info': props.isLoading,
          'is-warning': props.hasError,
          'text-info': props.isEdited,
        })}
        data-e2e-loading={props.isLoading ? 'true' : undefined}
        data-e2e={`${props.id}-select`}
        disabled={props.isDisabled || props.isLoading || props.isReadOnly}
        id={props.id}
        onBlur={props.onBlur}
        onChange={props.onChange}
        onClick={props.onClick}
        onFocus={props.onFocus}
        required={props.isRequired}
        tabIndex={props.tabIndex || 0}
        value={props.value || ''}
      >
        {items &&
          items.map((item, index) => (
            <option key={item.value ?? index} value={item.value} disabled={props.hasEmptyItem && index === 0}>
              {item.label || item.value}
            </option>
          ))}
      </select>
    </div>
  );
};

export const ListFieldWithQuoteFormWrapper = (
  props: IListFieldProps<any, string | number> & IWithQuoteFormWrapperProps,
) => (
  <WithQuoteFormWrapper
    colSize={props.colSize}
    id={props.id}
    isCustom={props.isCustom}
    label={props.label}
    onCustomizationRemoved={props.onCustomizationRemoved}
  >
    <ListField {...props} />
  </WithQuoteFormWrapper>
);
